import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await login(email, password);
      navigate('/home');
    } catch (err: any) {
      console.error('Login error:', err);
      setError('Failed to sign in. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-uc-navy relative overflow-hidden">
      {/* Background pattern overlay */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-0 right-0 w-1/2 h-full transform rotate-12 bg-uc-navy-light"></div>
        <div className="absolute bottom-0 left-0 w-1/2 h-full transform -rotate-12 bg-uc-navy-light"></div>
      </div>

      <div className="relative z-10 flex items-center justify-center min-h-screen px-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 w-full max-w-md">
          {/* Logo and Header */}
          <div className="flex items-center mb-6">
            <div className="w-16 h-16 bg-uc-navy rounded mr-4 flex items-center justify-center">
              <span className="text-uc-gold text-3xl font-bold">M</span>
            </div>
            <div>
              <h1 className="text-2xl font-bold text-uc-navy font-header">UC MERCED</h1>
              <p className="text-gray-600 text-sm">Single Sign On</p>
            </div>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 text-sm">
                {error}
              </div>
            )}

            <div className="mb-4">
              <label className="block text-uc-navy font-semibold mb-2 text-sm">
                Email (Required)
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="example@ucmerced.edu"
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-uc-navy text-sm"
                required
              />
            </div>

            <div className="mb-4">
              <label className="block text-uc-navy font-semibold mb-2 text-sm">
                Password (Required)
              </label>
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-uc-navy text-sm"
                required
              />
            </div>

            <div className="mb-6">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={showPassword}
                  onChange={(e) => setShowPassword(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm text-gray-700">Show Password</span>
              </label>
            </div>

            <div className="flex items-center justify-between mb-6">
              <a href="/forgot-password" className="text-uc-navy hover:underline text-sm">
                Forgot Password?
              </a>
              <button
                type="submit"
                disabled={loading}
                className="bg-uc-navy text-white px-8 py-2 rounded hover:bg-uc-navy-light transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-sm font-semibold"
              >
                {loading ? 'Signing In...' : 'Sign In'}
              </button>
            </div>

            <div className="text-center text-sm">
              <span className="text-gray-600">New to UC Merced? </span>
              <a href="/claim-account" className="text-uc-navy hover:underline font-semibold">
                Claim Account
              </a>
            </div>
          </form>
        </div>
      </div>

      <div className="absolute bottom-4 left-4 text-white text-xs">
        University of California, Merced
      </div>

      <div className="absolute bottom-4 right-4 text-white text-xs">
        © 2025 UC Regents • Privacy/Legal Notice
      </div>
    </div>
  );
};

export default LoginPage;
